import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppBar, Toolbar, Box, Typography, Chip } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { NAVIGATION_ITEMS, SITE_CONFIG } from '@/config';
import { useThemeName } from '@/theme';

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { themeName, setThemeName } = useThemeName();

  // メニュー開閉時のbodyスクロール制御
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const handleNavClick = (path: string) => {
    navigate(path);
    setMenuOpen(false);
  };

  const toggleTheme = () => {
    const next = themeName === 'turquoise' ? 'orinishiki' : 'turquoise';
    setThemeName(next);
    const url = new URL(window.location.href);
    url.searchParams.set('theme', next);
    window.history.replaceState({}, '', url.toString());
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: 'transparent',
          boxShadow: 'none',
          zIndex: 1300,
        }}
      >
        <Toolbar
          sx={{
            maxWidth: 1400,
            width: '100%',
            mx: 'auto',
            px: { xs: 2.5, md: 5 },
            py: { xs: 1.5, md: 2 },
            justifyContent: 'space-between',
            minHeight: { xs: 56, md: 64 },
          }}
        >
          {/* ロゴ */}
          <Box
            onClick={() => handleNavClick('/')}
            sx={{ cursor: 'pointer', zIndex: 1301 }}
          >
            <Typography
              sx={{
                fontFamily: '"Noto Serif JP", serif',
                fontSize: { xs: '0.875rem', md: '1rem' },
                fontWeight: 400,
                color: menuOpen ? '#fff' : 'text.primary',
                letterSpacing: '0.15em',
                transition: 'color 0.4s ease',
              }}
            >
              {SITE_CONFIG.company.name}
            </Typography>
            <Typography
              sx={{
                fontSize: '0.5rem',
                letterSpacing: '0.25em',
                color: menuOpen ? 'rgba(255,255,255,0.5)' : 'text.secondary',
                opacity: menuOpen ? 1 : 0.45,
                mt: 0.25,
                transition: 'all 0.4s ease',
              }}
            >
              {SITE_CONFIG.company.nameEn.toUpperCase()}
            </Typography>
          </Box>

          {/* Menu / Close ボタン */}
          <Box
            onClick={() => setMenuOpen(!menuOpen)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              zIndex: 1301,
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: '0.6875rem', md: '0.75rem' },
                letterSpacing: '0.12em',
                color: menuOpen ? '#fff' : 'text.primary',
                opacity: 0.7,
                transition: 'color 0.4s ease',
              }}
            >
              {menuOpen ? 'Close' : 'Menu'}
            </Typography>
            {/* ハンバーガー / × アイコン */}
            <Box
              sx={{
                width: 20,
                height: 14,
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  width: menuOpen ? 20 : 22,
                  height: '1.5px',
                  bgcolor: menuOpen ? '#fff' : 'text.primary',
                  opacity: 0.6,
                  transition: 'all 0.4s ease',
                  transform: menuOpen
                    ? 'rotate(45deg)'
                    : 'translateY(-3px)',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  width: menuOpen ? 20 : 16,
                  height: '1.5px',
                  bgcolor: menuOpen ? '#fff' : 'text.primary',
                  opacity: 0.6,
                  transition: 'all 0.4s ease',
                  transform: menuOpen
                    ? 'rotate(-45deg)'
                    : 'translateY(3px)',
                  ml: menuOpen ? 0 : 'auto',
                  right: 0,
                }}
              />
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      {/* 全画面メニューオーバーレイ */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ clipPath: 'inset(0 0 0 100%)' }}
            animate={{ clipPath: 'inset(0 0 0 0%)' }}
            exit={{ clipPath: 'inset(0 0 0 100%)' }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1299,
            }}
          >
            <Box
              sx={{
                width: '100%',
                height: '100%',
                bgcolor: themeName === 'turquoise'
                  ? 'rgba(38, 70, 83, 0.95)'
                  : 'rgba(61, 54, 48, 0.95)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {/* ナビゲーションリンク */}
              <Box
                component="nav"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: { xs: 3, md: 4 },
                }}
              >
                {NAVIGATION_ITEMS.map((item, i) => (
                  <motion.div
                    key={item.path}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.5,
                      delay: 0.2 + i * 0.06,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                  >
                    <Typography
                      onClick={() => handleNavClick(item.path)}
                      sx={{
                        fontFamily: '"Noto Serif JP", serif',
                        fontSize: { xs: '1.125rem', md: '1.375rem' },
                        fontWeight: 300,
                        letterSpacing: '0.08em',
                        color: '#fff',
                        cursor: 'pointer',
                        opacity: location.pathname === item.path ? 1 : 0.7,
                        transition: 'opacity 0.3s ease',
                        '&:hover': { opacity: 1 },
                      }}
                    >
                      {item.label}
                    </Typography>
                  </motion.div>
                ))}
              </Box>

              {/* テーマ切り替え */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.6 }}
              >
                <Box sx={{ mt: { xs: 6, md: 8 } }}>
                  <Chip
                    label={`Theme: ${themeName === 'turquoise' ? 'ターコイズ' : '織錦'}`}
                    onClick={toggleTheme}
                    variant="outlined"
                    sx={{
                      borderColor: 'rgba(255,255,255,0.3)',
                      color: 'rgba(255,255,255,0.6)',
                      cursor: 'pointer',
                      fontSize: '0.6875rem',
                      letterSpacing: '0.1em',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: 'rgba(255,255,255,0.7)',
                        color: '#fff',
                      },
                    }}
                  />
                </Box>
              </motion.div>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
